/**
 * Cliente HTTP do painel.
 * - Access token só em memória (nunca em localStorage).
 * - Refresh via cookie HttpOnly (__Host-ec_rt) + cabeçalho anti-CSRF.
 * - Uma única renovação em voo por vez, inclusive entre abas (Web Locks API): como o cookie é
 *   compartilhado entre abas, a segunda aba renova com o cookie já rotacionado pela primeira e
 *   não dispara a detecção de reuso do servidor.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly issues?: { path: string; message: string }[],
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface RequestOptions {
  body?: unknown;
  /** Envia X-Tenant-Id com a empresa selecionada. */
  tenant?: boolean;
  /** Token alternativo (ex.: mfaToken). Desliga a renovação automática. */
  bearer?: string;
  /** Não usar Authorization. */
  anonymous?: boolean;
  signal?: AbortSignal;
}

type Fetch = typeof fetch;

interface LockManagerLike {
  request<T>(name: string, cb: () => Promise<T>): Promise<T>;
}

export class ApiClient {
  accessToken: string | null = null;
  tenantId: string | null = null;
  /** Chamado quando a sessão não pode mais ser renovada (ex.: revogada). */
  onSessionLost: (() => void) | null = null;
  private refreshing: Promise<boolean> | null = null;

  constructor(
    readonly baseUrl: string,
    private readonly fetchImpl: Fetch = (...args) => fetch(...args),
    private readonly locks: LockManagerLike | undefined = typeof navigator !== 'undefined'
      ? (navigator as Navigator & { locks?: LockManagerLike }).locks
      : undefined,
  ) {}

  private async raw(method: string, path: string, opts: RequestOptions, token: string | null) {
    const headers: Record<string, string> = { accept: 'application/json' };
    if (opts.body !== undefined) headers['content-type'] = 'application/json';
    if (token) headers.authorization = `Bearer ${token}`;
    if (opts.tenant) {
      if (!this.tenantId) throw new ApiError(0, 'tenant_required', 'Selecione uma empresa');
      headers['x-tenant-id'] = this.tenantId;
    }
    if (path.startsWith('/v1/auth/refresh') || path.startsWith('/v1/auth/logout'))
      headers['x-ec-csrf'] = '1';
    return this.fetchImpl(`${this.baseUrl}${path}`, {
      method,
      headers,
      credentials: 'include',
      ...(opts.body !== undefined ? { body: JSON.stringify(opts.body) } : {}),
      ...(opts.signal ? { signal: opts.signal } : {}),
    });
  }

  private static async parse<T>(res: Response): Promise<T> {
    if (res.status === 204) return undefined as T;
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new ApiError(
        res.status,
        typeof data.error === 'string' ? data.error : 'error',
        typeof data.message === 'string' ? data.message : `Erro ${res.status}`,
        data.issues,
      );
    }
    return data as T;
  }

  /** Renova o access token. Retorna false se a sessão acabou. */
  refresh(): Promise<boolean> {
    this.refreshing ??= (async () => {
      const run = async () => {
        const res = await this.raw('POST', '/v1/auth/refresh', {}, null);
        if (!res.ok) {
          this.accessToken = null;
          return false;
        }
        const data = (await res.json()) as { accessToken: string };
        this.accessToken = data.accessToken;
        return true;
      };
      try {
        return this.locks ? await this.locks.request('endcopilot-refresh', run) : await run();
      } catch {
        return false;
      } finally {
        this.refreshing = null;
      }
    })();
    return this.refreshing;
  }

  async request<T>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
    const token = opts.anonymous ? null : (opts.bearer ?? this.accessToken);
    let res = await this.raw(method, path, opts, token);
    const canRenew = !opts.anonymous && !opts.bearer && res.status === 401;
    if (canRenew) {
      if (await this.refresh()) {
        res = await this.raw(method, path, opts, this.accessToken);
      } else {
        this.onSessionLost?.();
      }
    }
    return ApiClient.parse<T>(res);
  }

  get<T>(path: string, opts?: RequestOptions) {
    return this.request<T>('GET', path, opts);
  }

  post<T>(path: string, body: unknown = {}, opts: RequestOptions = {}) {
    return this.request<T>('POST', path, { ...opts, body });
  }
}

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://127.0.0.1:3000';
export const api = new ApiClient(API_URL);
